/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { useTranslationStore } from '../../../stores';
import { MouseEvent, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation as t } from '../../../hooks/useTranslation';
import styles from '../../../templates/EpIncidentSteps/EpIncidentSteps.module.scss';
import {
  EpButton,
  EpErrorMessage,
  EpPatientData,
  EpStepContent,
} from '../components';
import { EpFormInput } from '../components/EpFormInput';
import { EpLoading } from '../../EpLoading';
import { globalizationService } from '../../../services/globalizationService';
import PatientDocumentType from './PatientDocumentType';
import clsx from 'clsx';

export default function NotificationRefersToIdInput({
  isActive = false,
  translations,
  isClinicalPharmacy = false,
}: {
  isActive?: boolean;
  translations: string[];
  isClinicalPharmacy?: boolean;
}) {
  const tag = 'PSNotificationID';

  const {
    translations: { global: globalTranslations },
  } = useTranslationStore();
  const { translations: translationPSNotificationID } =
    globalizationService.getTranslationsByTag(tag);

  const PersonValue = useWatch({ name: 'person.inputValue' });
  const NoId = useWatch({ name: 'noId' });
  const personType = useWatch({ name: 'personType.inputValue' });
  const activeStep = useWatch({ name: 'formUtils.activeStep' });
  const idIsValid = useWatch({ name: 'notificationRefersToId.isValid' });
  const identificationType = useWatch({
    name: 'IdentificationType.inputValue',
  });

  const identificationTypeLabel = useWatch({
    name: 'IdentificationType.inputLabel',
  });

  const isLoadingMedicalRecord = useWatch({ name: 'isLoadingMedicalRecord' });
  const checkIfIsPersonType = personType === '1' && !isClinicalPharmacy;

  const isVisible = PersonValue === 'true' && !NoId && checkIfIsPersonType;
  const {
    register,
    unregister,
    setValue,
    getValues,
    resetField,
    formState: { errors },
  } = useFormContext();

  const [patientData, setPatientData] = useState<string | null>(
    getValues('notificationRefersToId.inputValue'),
  );

  function handlePatientData(e?: MouseEvent) {
    e?.preventDefault();
    const idField = getValues('notificationRefersToId');

    if (!patientData) setPatientData(idField.inputValue);
    else {
      setPatientData(null);
      setValue('notificationRefersToId', {
        inputValue: '',
        isValid: undefined,
      });
    }
    setValue('formUtils.stepNeedReview', '2');
    resetField('unitId.inputValue');
    resetField('unitId.inputLabel');
  }

  useEffect(() => {
    if (personType && activeStep === '1') {
      setPatientData(null);
      setValue('notificationRefersToId', {
        inputValue: '',
        isValid: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personType, activeStep]);

  useEffect(() => {
    if (!NoId && isClinicalPharmacy) {
      register('notificationRefersToId.isValid', {
        required: t(translations, 'Service number must be valid'),
      });
    } else if (
      (PersonValue === 'true' && !!NoId) ||
      PersonValue === 'false' ||
      PersonValue === undefined ||
      personType !== '1'
    ) {
      unregister('notificationRefersToId.isValid');
    } else
      register('notificationRefersToId.isValid', {
        required: t(translations, 'Service number must be valid'),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PersonValue, NoId, personType, translations]);

  if (!isActive && !isVisible) {
    return null;
  }
  const handleKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      handlePatientData(event);
    }
  };

  return (
    <>
      <div
        className={`${styles['ep-notification-id-search']} align-items-start`}
      >
        <PatientDocumentType
          translations={translationPSNotificationID}
          isPharmacy={isClinicalPharmacy}
        />
        <EpStepContent.Label
          label={
            identificationTypeLabel
              ? identificationTypeLabel
              : t(translations, 'Service Number:')
          }
        >
          <EpFormInput
            inputId="notificationRefersToId"
            inputName="notificationRefersToId.inputValue"
            inputType="text"
            inputPlaceholder={
              identificationTypeLabel
                ? identificationTypeLabel
                : t(translations, 'Service Nº')
            }
            translations={translations}
            inputRequired={!!patientData}
            inputIsDisabled={!identificationType || !!patientData}
            errorMessage={t(translations, '* Required Fied')}
            inputIsFullWidth
            onKeyDown={(event) => handleKeyPress(event)}
            inputWithError={!!errors[`notificationRefersToId`]}
            className=""
            // inputIsDisabled={false}
            // inputIsDisabled={true}
          />
        </EpStepContent.Label>

        {isLoadingMedicalRecord ? (
          <div
            style={{ width: '20px', marginLeft: '24px', marginBottom: '28px' }}
            className={styles['ep-notification-id-search__button']}
          >
            <EpLoading
              position={'relative'}
              loadingType="card"
              loadingId={'loading--page'}
              isVisible
              size="sm"
            />
          </div>
        ) : (
          <EpStepContent.Label label="&nbsp;">
            <EpButton
              buttonType="primary-outline"
              buttonText={
                !patientData
                  ? identificationTypeLabel
                    ? t(translations, 'Search {0}').replace('{0}',identificationTypeLabel)
                    : t(translations, 'Search Service Number')
                  : t(globalTranslations, 'Clear')
              }
              buttonIsDisabled={!identificationType}
              buttonSize="md"
              buttonLeftIconName={!patientData ? 'SolidSearch' : 'SolidEraser'}
              buttonOnClickFunc={(e: MouseEvent) => handlePatientData(e)}
              buttonId={'ep-button__notification--ref'}
              buttonActionType="button"
              className={clsx(styles['ep-notification-id-search__button-md'])}
            />
          </EpStepContent.Label>
        )}
      </div>
      {/* <EpErrorMessage inputName='notificationRefersToId.inputValue' /> */}
      {!idIsValid && (
        <EpErrorMessage inputName="notificationRefersToId.isValid" />
      )}
      {patientData && (
        <EpPatientData
          medicalRecord={patientData}
          identificationType={identificationType}
          translations={translations}
        />
      )}
    </>
  );
}
